/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '@/store';
import { PreConfiguredMeal, Menu, MenuItemLookupResult } from '@/models';
import { prefixValues } from '@/helpers/util';
import { IngredientsMetadataForChannel, MenuMetadataForChannel } from '@/models/metadata';
import { INGREDIENTS_METADATA_COUNTRIES, MENU_METADATA_CHANNELS } from '@/services/metadata/metadata.service';

export const MUTATION_NAMES = {
  SET_PRECONFIGURED_MEALS: 'setPreconfiguredMeals',
  CACHE_MENU_FOR_RESTAURANT: 'cacheMenuForRestaurant',
  REMOVE_CACHED_MENU_FOR_RESTAURANT: 'removeCachedMenuForRestaurant',
  SET_CACHED_MENU_RESTAURANT_IDS: 'setCachedMenuRestaurantIds',
  CACHE_UNIVERSAL_MENU: 'cacheUniversalMenu',
  CACHE_UNIVERSAL_MENU_FULL: 'cacheUniversalMenuFull',
  SET_ACTIVE_MENU: 'setActiveMenu',
  SET_METADATA: 'setMetadata',
  SET_METADATA_WITH_NUTRITION: 'setMetadataWithNutrition',
  SET_MENU_ITEM_LOOKUP_MAP: 'setMenuLookupMap',
  IS_LOADING_RESTAURANT_MENU: 'isLoadingRestaurantMenu',
  SET_INGREDIENTS_METADATA: 'setIngredientsMetadata'
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, 'menu/');

export interface MenuModuleState {
  preConfiguredMeals: PreConfiguredMeal[];
  universalMenu: {
    etag: string;
    menu: Menu;
  } | null;
  universalMenuFull: {
    etag: string;
    menu: Menu;
  } | null;
  cachedMenus: {
    [restaurantId: number]: {
      etag: string;
      menu: Menu;
    };
  };
  cachedMenuRestaurantIds: number[];
  activeMenu: Menu | null;
  menuItemLookupMap: Map<string, MenuItemLookupResult> | null;
  isLoadingRestaurantMenu: boolean;

  metadata: MenuMetadataForChannel | null;
  metadataETag: string | null;
  cachedMetadata: {
    [channel: string]: {
      data?: MenuMetadataForChannel | null;
      etag?: string | null;
    }
  };
  ingredientsMetadata: {
    [country: string]: {
    data?: IngredientsMetadataForChannel | null;
    etag?: string | null;
  }}
}

export interface MenuCacheEntry {
  etag: string;
  menu: Menu;
}

export type RestaurantMenuCacheEntry = MenuCacheEntry & { restaurantId: number };

const DEFAULT_STATE: MenuModuleState = {
  preConfiguredMeals: [],
  cachedMenus: {},
  cachedMenuRestaurantIds: [],
  activeMenu: null,
  universalMenu: null,
  universalMenuFull: null,
  menuItemLookupMap: null,
  isLoadingRestaurantMenu: false,

  metadata: null,
  metadataETag: null,
  cachedMetadata: {
    [MENU_METADATA_CHANNELS.DESKTOP]: {},
    [MENU_METADATA_CHANNELS.MOBILE]: {},
    [`${MENU_METADATA_CHANNELS.DESKTOP}-nutrition`]: {},
    [`${MENU_METADATA_CHANNELS.MOBILE}-nutrition`]: {},
  },
  ingredientsMetadata: {
    [INGREDIENTS_METADATA_COUNTRIES.US]: {}
  }
};

export function makeMenuModule(): Module<MenuModuleState, AppState> {
  return {
    namespaced: true,
    state: DEFAULT_STATE,
    mutations: {
      [MUTATION_NAMES.SET_PRECONFIGURED_MEALS]: (state, preConfiguredMeals) => {
        state.preConfiguredMeals = preConfiguredMeals;
      },
      [MUTATION_NAMES.CACHE_MENU_FOR_RESTAURANT]: (state, entry: RestaurantMenuCacheEntry) => {
        state.cachedMenus[entry.restaurantId] = {
          etag: entry.etag,
          menu: entry.menu,
        };
      },
      [MUTATION_NAMES.REMOVE_CACHED_MENU_FOR_RESTAURANT]: (state, restaurantId: number) => {
        delete state.cachedMenus[restaurantId];
      },
      [MUTATION_NAMES.SET_CACHED_MENU_RESTAURANT_IDS]: (state, newIndex: number[]) => {
        state.cachedMenuRestaurantIds = newIndex;
      },
      [MUTATION_NAMES.CACHE_UNIVERSAL_MENU]: (state, entry: MenuCacheEntry) => {
        state.universalMenu = entry;
      },
      [MUTATION_NAMES.CACHE_UNIVERSAL_MENU_FULL]: (state, entry: MenuCacheEntry) => {
        state.universalMenuFull = entry;
      },
      [MUTATION_NAMES.SET_ACTIVE_MENU]: (state, menu: Menu) => {
        state.activeMenu = menu;
      },
      [MUTATION_NAMES.SET_METADATA]: (state, args: { metadata: MenuMetadataForChannel, etag: string }) => {
        state.metadata = args.metadata;        
        state.metadataETag = args.etag;

        state.cachedMetadata[args.metadata.channel] = {
          data: args.metadata,
          etag: args.etag,
        };
      },
      [MUTATION_NAMES.SET_METADATA_WITH_NUTRITION]: (state, args: { metadata: MenuMetadataForChannel, etag: string }) => {
        state.cachedMetadata[`${args.metadata.channel}-nutrition`] = {
          data: args.metadata,
          etag: args.etag,
        };
      },
      [MUTATION_NAMES.SET_MENU_ITEM_LOOKUP_MAP]: (state, menuItemLookupMap: Map<string, MenuItemLookupResult>) => {
        state.menuItemLookupMap = menuItemLookupMap;
      },
      [MUTATION_NAMES.IS_LOADING_RESTAURANT_MENU]: (state, isLoadingRestaurantMenu: boolean) => {
        state.isLoadingRestaurantMenu = isLoadingRestaurantMenu;
      },
      [MUTATION_NAMES.SET_INGREDIENTS_METADATA]: (state, args: { metadata: IngredientsMetadataForChannel, etag: string }) => {
        state.ingredientsMetadata[INGREDIENTS_METADATA_COUNTRIES.US] = { data: args.metadata, etag: args.etag };  
      }
    },
  };
}
